/**
 * QuizStartScreen Component
 * Quiz type selection screen
 */

export interface QuizStartScreenProps {
  onStartQuiz: (quizType: 'random' | 'new' | 'all') => void;
}

export const QuizStartScreen = ({ onStartQuiz }: QuizStartScreenProps) => {
  const quizTypes = [
    {
      type: 'random' as const,
      title: '10 Random Members',
      description: 'Quiz with 10 randomly selected team members',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      ),
      colorClass: 'hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:ring-blue-500',
      bgClass: 'bg-status-info',
      textClass: 'text-blue-600 dark:text-blue-400',
    },
    {
      type: 'new' as const,
      title: 'New Members',
      description: 'Members who joined less than 1 year ago',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
          />
        </svg>
      ),
      colorClass: 'hover:border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 focus:ring-green-500',
      bgClass: 'bg-status-success',
      textClass: 'text-green-600 dark:text-green-400',
    },
    {
      type: 'all' as const,
      title: 'All Members',
      description: 'Quiz with all team members',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      colorClass: 'hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 focus:ring-purple-500',
      bgClass: 'bg-status-accent',
      textClass: 'text-purple-600 dark:text-purple-400',
    },
  ];

  return (
    <div className="bg-surface rounded-lg shadow p-8 text-center">
      <div className="mb-6">
        <img
          src="/TW quiz illustration.png"
          alt="Quiz illustration"
          className="mx-auto mb-4"
          style={{ height: '150px' }}
        />
        <h2 className="text-2xl font-bold text-heading mb-2">Select Quiz Type</h2>
        <p className="text-meta">
          Each quiz will test your knowledge of team members with photo recognition questions.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {quizTypes.map((quiz) => (
          <button
            key={quiz.type}
            onClick={() => onStartQuiz(quiz.type)}
            className={`p-6 text-center border-2 border-[var(--border-default)] rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 ${quiz.colorClass}`}
          >
            <div
              className={`${quiz.bgClass} rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3`}
            >
              <span className={quiz.textClass}>{quiz.icon}</span>
            </div>
            <h3 className="font-medium text-heading mb-2">{quiz.title}</h3>
            <p className="text-sm text-meta">{quiz.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
};
